import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { normalizeLanguageCode } from '../../../i18n/language';
import { useThemeMode } from '../../context/ThemeModeContext';
import SupportModal from '../../redux/containers/support/SupportModal';
import { BRAND_ACCENT } from '../../styleSheet';
import AvatarBadge from '../templates/AvatarBadge';
import Logo from './Logo';

const { REACT_APP_AUTH_BASE_URL } = process.env;

const LANGUAGES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ja', label: '日本語' },
];

const THEME_OPTIONS = [
  { mode: 'light', icon: 'sunny-outline' },
  { mode: 'dark', icon: 'moon-outline' },
  { mode: 'system', icon: 'contrast-outline' },
];

function getInitials(sessionInfo) {
  const rawName = sessionInfo?.name?.trim();
  if (rawName) {
    const [first = '', last = ''] = rawName.split(/\s+/);
    return `${first.charAt(0)}${last.charAt(0)}`.trim().toUpperCase() || first.charAt(0).toUpperCase();
  }
  const email = sessionInfo?.email || '';
  return email ? email.charAt(0).toUpperCase() : '?';
}

const StyledMenu = styled(props => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.bruno.chrome.menuSurface,
    borderRadius: 12,
    marginTop: theme.spacing(1),
    minWidth: 280,
    color: theme.bruno.colors.textInverse,
    border: `1px solid ${theme.bruno.chrome.menuDivider}`,
  },
  '& .MuiDivider-root': { borderColor: theme.bruno.chrome.menuDivider },
  '& .MuiMenuItem-root:hover': { backgroundColor: theme.bruno.chrome.menuHover },
  '& .MuiMenuItem-root': {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: theme.bruno.chrome.menuSelected,
    fontWeight: 700,
  },
  '& .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: theme.bruno.chrome.menuSelectedHover,
  },
}));

export default function ContentTopBar({ onHamburgerClick, sessionInfo, logOut }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { themeMode, updateThemeMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const chromeFg = theme.bruno.chrome.appBarForeground;
  const fullName = sessionInfo?.name || sessionInfo?.email || '';
  const email = sessionInfo?.email || '';
  const tenant = sessionInfo?.firebase?.tenant;
  const initials = getInitials(sessionInfo);

  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const currentLanguage = normalizeLanguageCode(i18n.language) || 'en';

  const handleMenuOpen = e => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLangOpen = e => { e.stopPropagation(); setLangAnchorEl(e.currentTarget); };
  const handleLangClose = () => setLangAnchorEl(null);
  const handleSupportOpen = () => { handleMenuClose(); setSupportOpen(true); };

  const handleLanguageChange = code => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('bruno_language', code); } catch { }
    handleLangClose();
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logOut();
    window.location.replace(`${REACT_APP_AUTH_BASE_URL}/ext?redirect=${process.env.REACT_APP_BASE_URL}`);
  };

  const handleAccountClick = () => {
    window.location.href = `${REACT_APP_AUTH_BASE_URL}/profile?tenant=${tenant}&redirect=${window.location.href}`;
    handleMenuClose();
  };

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          minHeight: { xs: 48, md: 56 },
          mb: 2.25,
          bgcolor: 'background.default',
        }}
      >
        {/* Left side: hamburger (mobile only) + logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {isMobile && (
            <IconButton
              onClick={onHamburgerClick}
              aria-label="open navigation"
              sx={{
                width: 38,
                height: 38,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          <Logo onClick={() => navigate('/')} />
        </Box>

        {/* Avatar */}
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            p: 0,
            borderRadius: '50%',
            boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 1px 3px rgba(0,0,0,0.08)`,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <AvatarBadge initials={initials} size={34} textVariant="caption" />
        </IconButton>
      </Box>

      {/* Account dropdown */}
      <StyledMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, textAlign: 'center' }}>
          <AvatarBadge initials={initials} size={56} textVariant="subtitle1" sx={{ mx: 'auto', mb: 1.5 }} />
          {fullName && (
            <Typography variant="subtitle2" sx={{ color: chromeFg }}>{fullName}</Typography>
          )}
          {email && (
            <Typography variant="body2" sx={{ color: theme.bruno.colors.textInverseSub, mt: 0.5 }}>{email}</Typography>
          )}
        </Box>

        <Divider />

        <MenuItem onClick={handleAccountClick}>
          <IconComponent iconName="pencil-outline" style={{ color: chromeFg, fontSize: 22 }} />
          <Typography variant="body1" sx={{ ml: 1.5, color: chromeFg }}>{t('bruno.drawer.account', 'Edit Profile')}</Typography>
        </MenuItem>

        <MenuItem onClick={handleLangOpen}>
          <IconComponent iconName="globe-outline" style={{ color: chromeFg, fontSize: 22 }} />
          <Typography variant="body1" sx={{ ml: 1.5, color: chromeFg }}>{t('bruno.account.language', 'Language')}</Typography>
        </MenuItem>

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', px: 2, py: 1.5 }}>
          {THEME_OPTIONS.map(option => {
            const isActive = themeMode === option.mode;
            return (
              <IconButton
                key={option.mode}
                size="small"
                onClick={() => updateThemeMode(option.mode)}
                sx={{
                  borderRadius: 2,
                  px: 1.25,
                  py: 0.9,
                  minWidth: 40,
                  minHeight: 40,
                  bgcolor: isActive ? `${BRAND_ACCENT}22` : 'transparent',
                  color: isActive ? BRAND_ACCENT : chromeFg,
                }}
              >
                <IconComponent iconName={option.icon} style={{ fontSize: 20 }} />
              </IconButton>
            );
          })}
        </Box>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <IconComponent iconName="log-out-outline" style={{ color: chromeFg, fontSize: 22 }} />
          <Typography variant="body1" sx={{ ml: 1.5, color: chromeFg }}>{t('bruno.account.logout', 'Log Out')}</Typography>
        </MenuItem>
      </StyledMenu>

      {/* Language submenu */}
      <StyledMenu anchorEl={langAnchorEl} open={Boolean(langAnchorEl)} onClose={handleLangClose}>
        {LANGUAGES.map(lang => (
          <MenuItem
            key={lang.code}
            selected={currentLanguage === lang.code}
            onClick={() => handleLanguageChange(lang.code)}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: currentLanguage === lang.code ? 600 : 400,
                color: currentLanguage === lang.code ? BRAND_ACCENT : 'inherit',
              }}
            >
              {lang.label}
            </Typography>
          </MenuItem>
        ))}
      </StyledMenu>

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
